import { useState, useEffect, useCallback, useMemo } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { getAllTimeEntries, approveTimeEntry, rejectTimeEntry, getScheduledUnlogged, updateAdminTimeEntry, deleteAdminTimeEntry, resolveTimeEntryDispute, type TimeEntry, type ScheduledUnlogged } from '@/lib/api/portal';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { BillingTally } from '@/components/timesheets/BillingTally';
import { getEmployees, type Employee } from '@/lib/api/employees';
import { getJobs, type Job } from '@/lib/api/jobs';
import { useAuth } from '@/lib/hooks/useAuth';
import { useToast } from '@/lib/hooks/useToast';
import { ToastContainer } from '@/components/common/Toast';
import { DayLogModal, type DayEntry, type SaveResult } from '@/components/schedule/DayLogModal';

type AdminEntry = TimeEntry & { employee_email: string; employee_name: string };

const statusBadge: Record<string, string> = {
  submitted: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  draft: 'bg-gray-100 text-gray-600',
};

const sourceBadge: Record<string, string> = {
  sms: 'bg-purple-100 text-purple-700',
  portal: 'bg-sky-100 text-sky-700',
  admin: 'bg-orange-100 text-orange-700',
};

type Filter = 'submitted' | 'approved' | 'rejected' | 'all';

export const TimesheetApprovalsPage = () => {
  const [entries, setEntries] = useState<AdminEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  const [filter, setFilter] = useState<Filter>('submitted');
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  // #105 — structured filters + row selection for tallying billable hours
  const [empFilter, setEmpFilter] = useState('');
  const [jobFilter, setJobFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Scheduled-but-unlogged gaps + hours-logging modal
  const { user } = useAuth();
  const { toast, toasts, removeToast } = useToast();
  const [gaps, setGaps] = useState<ScheduledUnlogged[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [modalDate, setModalDate] = useState<string | null>(null);
  const [modalJobId, setModalJobId] = useState<number | null>(null);
  // CR #116/#118 — full CRUD: create on any date, delete with confirm
  const [addDate, setAddDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [confirmDelete, setConfirmDelete] = useState<AdminEntry | null>(null);
  const doDelete = async () => {
    if (!confirmDelete) return;
    const id = confirmDelete.id;
    setConfirmDelete(null);
    try {
      await deleteAdminTimeEntry(id);
      toast.success('Entry deleted');
      load();
    } catch { toast.error('Failed to delete entry'); }
  };
  // Edit-entry modal
  const [editEntry, setEditEntry] = useState<AdminEntry | null>(null);
  const [editForm, setEditForm] = useState({ work_date: '', start_time: '', end_time: '', break_minutes: '0', hours: '', job_id: '', notes: '' });
  const [editBusy, setEditBusy] = useState(false);
  const openEdit = (e: AdminEntry) => {
    setEditEntry(e);
    setEditForm({
      work_date: String(e.work_date).slice(0, 10),
      start_time: e.start_time ? String(e.start_time).slice(0, 5) : '',
      end_time: e.end_time ? String(e.end_time).slice(0, 5) : '',
      break_minutes: String(e.break_minutes ?? 0),
      hours: String(e.hours),
      job_id: e.job_id ? String(e.job_id) : '',
      notes: e.notes || '',
    });
  };
  const saveEdit = async () => {
    if (!editEntry) return;
    setEditBusy(true);
    try {
      await updateAdminTimeEntry(editEntry.id, {
        work_date: editForm.work_date,
        start_time: editForm.start_time || null,
        end_time: editForm.end_time || null,
        break_minutes: Number(editForm.break_minutes) || 0,
        hours: Number(editForm.hours) || 0,
        job_id: editForm.job_id ? Number(editForm.job_id) : null,
        notes: editForm.notes.trim() || undefined,
      });
      if (editEntry.disputed) await resolveTimeEntryDispute(editEntry.id).catch(() => {});
      toast.success('Entry updated');
      setEditEntry(null);
      load();
    } catch { toast.error('Failed to update entry'); }
    finally { setEditBusy(false); }
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [ents, g] = await Promise.all([getAllTimeEntries(), getScheduledUnlogged()]);
      setEntries(ents);
      setGaps(g);
    } catch {
      setError('Failed to load time entries. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getEmployees().then(e => setEmployees(e.filter(x => x.status === 'active'))).catch(() => {});
    getJobs({ limit: 200 }).then(r => setJobs(r.jobs)).catch(() => {});
  }, []);

  const entriesByDate = useMemo(() => {
    const m = new Map<string, DayEntry[]>();
    for (const e of entries) {
      const key = String(e.work_date).slice(0, 10);
      const arr = m.get(key) || [];
      arr.push(e as DayEntry);
      m.set(key, arr);
    }
    return m;
  }, [entries]);

  const myEmployeeId = useMemo(() => {
    const email = user?.email?.toLowerCase();
    return employees.find(e => e.email?.toLowerCase() === email)?.id ?? null;
  }, [employees, user]);

  const sortedEmployees = useMemo(() =>
    [...employees].sort((a, b) => {
      if (a.id === myEmployeeId) return -1;
      if (b.id === myEmployeeId) return 1;
      return a.name.localeCompare(b.name);
    }), [employees, myEmployeeId]);

  const gapDateLabel = (key: string) =>
    new Date(key + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

  const handleModalSaved = (result: SaveResult) => {
    setModalDate(null);
    if (result.noWork) toast.success('Day marked as no work');
    if (result.ok > 0) toast.success(`Logged ${result.ok} time entr${result.ok === 1 ? 'y' : 'ies'} — auto-approved`);
    if (result.failed.length > 0) toast.error(`Failed for: ${result.failed.join(', ')}`);
    load();
  };

  const handle = async (id: number, action: 'approve' | 'reject') => {
    setBusy(id);
    setError('');
    try {
      if (action === 'approve') await approveTimeEntry(id);
      else await rejectTimeEntry(id);
      await load();
    } catch {
      setError(`Failed to ${action} entry. Please try again.`);
    } finally {
      setBusy(null);
    }
  };

  const pending = entries.filter(e => e.status === 'submitted').length;

  const visible = entries.filter(e => {
    if (filter !== 'all' && e.status !== filter) return false;
    if (empFilter && e.employee_email !== empFilter) return false;
    if (jobFilter && String(e.job_id ?? '') !== jobFilter) return false;
    if (sourceFilter && e.source !== sourceFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!e.employee_name?.toLowerCase().includes(q) && !e.employee_email?.toLowerCase().includes(q) && !e.job_title?.toLowerCase().includes(q)) return false;
    }
    // work_date can arrive as an ISO datetime — compare date part only
    if (dateFrom && String(e.work_date).slice(0, 10) < dateFrom) return false;
    if (dateTo && String(e.work_date).slice(0, 10) > dateTo) return false;
    return true;
  });

  const totalHours = visible.reduce((s, e) => s + Number(e.hours), 0);

  // Billable $ = hours × the employee's rate (matched by email — the entries
  // carry user identity, the rate lives on the employee record)
  const rateFor = (e: AdminEntry) =>
    Number(employees.find(emp => emp.email === e.employee_email)?.pay_rate ?? 0);
  const totalBillable = visible.reduce((s, e) => s + Number(e.hours) * rateFor(e), 0);
  const selectedRows = visible.filter(e => selected.has(e.id));
  const selectedHours = selectedRows.reduce((s, e) => s + Number(e.hours), 0);
  const selectedBillable = selectedRows.reduce((s, e) => s + Number(e.hours) * rateFor(e), 0);
  const allVisibleSelected = visible.length > 0 && visible.every(e => selected.has(e.id));
  const toggleAllVisible = () =>
    setSelected(prev => {
      const n = new Set(prev);
      if (allVisibleSelected) visible.forEach(e => n.delete(e.id));
      else visible.forEach(e => n.add(e.id));
      return n;
    });
  const toggleRow = (id: number) =>
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });

  const filters: { id: Filter; label: string }[] = [
    { id: 'submitted', label: `Pending (${pending})` },
    { id: 'approved', label: 'Approved' },
    { id: 'rejected', label: 'Rejected' },
    { id: 'all', label: 'All' },
  ];

  return (
    <MainLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Timesheet Approvals</h1>
            <p className="text-sm text-gray-500 mt-0.5">Review and approve employee time entries submitted via portal or SMS.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {pending > 0 && (
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
                <span className="material-symbols-outlined text-amber-500 text-lg">pending_actions</span>
                <span className="text-sm font-semibold text-amber-800">{pending} pending</span>
              </div>
            )}
            {/* CR #116/#118 — create entries for any employee on any date */}
            <div className="flex items-stretch rounded-lg overflow-hidden border border-primary/40">
              <input
                type="date"
                value={addDate}
                onChange={e => setAddDate(e.target.value)}
                className="h-10 px-2 text-sm border-r border-primary/20 bg-white text-gray-700 focus:outline-none"
                title="Date to log entries for"
              />
              <button
                onClick={() => { setModalJobId(null); setModalDate(addDate); }}
                className="flex items-center gap-1.5 h-10 px-4 bg-primary text-white text-sm font-semibold hover:opacity-90"
              >
                <span className="material-symbols-outlined text-base">add</span>
                Add entries
              </button>
            </div>
          </div>
        </div>

        {/* CR #136 — crew-rate billing tally */}
        <BillingTally />

        {/* Scheduled but no hours recorded */}
        {gaps.length > 0 && (
          <div className="bg-white rounded-xl border border-amber-200 overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3 bg-amber-50 border-b border-amber-100">
              <span className="material-symbols-outlined text-amber-500 text-lg">report</span>
              <h2 className="text-sm font-bold text-amber-900">Scheduled but no hours recorded</h2>
              <span className="text-xs font-semibold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">{gaps.length}</span>
              <span className="ml-auto text-xs text-amber-700/70">all scheduled days</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-xs text-gray-500 uppercase tracking-wide bg-gray-50">
                    <th className="text-left px-5 py-2.5 font-semibold">Date</th>
                    <th className="text-left px-5 py-2.5 font-semibold">Scheduled job</th>
                    <th className="text-left px-5 py-2.5 font-semibold hidden sm:table-cell">Crew / workers</th>
                    <th className="text-left px-5 py-2.5 font-semibold hidden md:table-cell">Time</th>
                    <th className="px-5 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {gaps.map((g, i) => (
                    <tr key={`${g.job_id}-${g.work_date}-${i}`} className="hover:bg-amber-50/40">
                      <td className="px-5 py-2.5 text-gray-700 whitespace-nowrap">{gapDateLabel(g.work_date)}</td>
                      <td className="px-5 py-2.5 font-medium text-gray-900 max-w-[240px] truncate" title={g.job_title}>{g.job_title}</td>
                      <td className="px-5 py-2.5 text-gray-500 hidden sm:table-cell max-w-[200px] truncate" title={g.workers.join(', ')}>
                        {g.workers.length ? g.workers.join(', ') : (g.crew_name || '—')}
                      </td>
                      <td className="px-5 py-2.5 text-gray-400 hidden md:table-cell whitespace-nowrap">
                        {g.start_time && g.end_time ? `${String(g.start_time).slice(0, 5)}–${String(g.end_time).slice(0, 5)}` : '—'}
                      </td>
                      <td className="px-5 py-2.5 text-right">
                        <button
                          onClick={() => { setModalDate(g.work_date); setModalJobId(g.job_id); }}
                          className="text-xs font-semibold text-primary border border-primary/30 bg-primary/5 hover:bg-primary/10 rounded-lg px-3 py-1.5 whitespace-nowrap"
                        >
                          Log hours
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-between gap-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            <span>{error}</span>
            <button onClick={load} className="text-xs font-semibold text-red-700 border border-red-300 rounded px-2 py-1 hover:bg-red-100">Retry</button>
          </div>
        )}

        {/* Filters row */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {filters.map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${filter === f.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="relative">
            <span className="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-base">search</span>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search employee or job…"
              className="border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 w-52"
            />
          </div>

          <div className="flex items-center gap-2">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            <span className="text-gray-400 text-sm">–</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>

          {/* Structured filters (#105) */}
          <select value={empFilter} onChange={e => setEmpFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/30 max-w-[11rem]">
            <option value="">All employees</option>
            {employees.map(emp => <option key={emp.id} value={emp.email}>{emp.name}</option>)}
          </select>
          <select value={jobFilter} onChange={e => setJobFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/30 max-w-[11rem]">
            <option value="">All jobs</option>
            {jobs.map(j => <option key={j.id} value={String(j.id)}>{j.title}</option>)}
          </select>
          <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/30">
            <option value="">All sources</option>
            <option value="portal">Portal</option>
            <option value="sms">SMS</option>
            <option value="admin">Admin</option>
          </select>

          {(searchQuery || dateFrom || dateTo || empFilter || jobFilter || sourceFilter) && (
            <button onClick={() => { setSearchQuery(''); setDateFrom(''); setDateTo(''); setEmpFilter(''); setJobFilter(''); setSourceFilter(''); }}
              className="text-xs text-gray-500 hover:text-red-500 flex items-center gap-1">
              <span className="material-symbols-outlined text-sm">close</span>Clear
            </button>
          )}
        </div>

        {/* Tally strip (#105): filtered totals + a live tally of checked rows */}
        {visible.length > 0 && (
          <div className="flex items-center gap-4 text-xs text-gray-500 border-b border-gray-100 pb-2 flex-wrap">
            <span>{visible.length} entr{visible.length !== 1 ? 'ies' : 'y'}</span>
            <span>Total: <strong className="text-gray-700">{totalHours.toFixed(1)}h</strong></span>
            <span>Billable: <strong className="text-gray-700">${totalBillable.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> <span className="text-gray-400">(at crew rates)</span></span>
            {selected.size > 0 && (
              <span className="ml-auto flex items-center gap-3 bg-primary/10 text-primary font-semibold rounded-full px-3 py-1">
                {selectedRows.length} selected · {selectedHours.toFixed(1)}h · ${selectedBillable.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                <button onClick={() => setSelected(new Set())} className="text-primary/70 hover:text-primary" title="Clear selection">
                  <span className="material-symbols-outlined text-sm leading-none">close</span>
                </button>
              </span>
            )}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center gap-4">
                  <div className="h-4 bg-gray-100 rounded animate-pulse w-1/4" />
                  <div className="h-4 bg-gray-100 rounded animate-pulse w-1/6" />
                  <div className="h-4 bg-gray-100 rounded animate-pulse w-1/12" />
                  <div className="h-4 bg-gray-100 rounded animate-pulse w-1/5" />
                  <div className="h-4 bg-gray-100 rounded animate-pulse w-1/6 ml-auto" />
                </div>
              ))}
            </div>
          ) : visible.length === 0 ? (
            <div className="p-10 text-center">
              <span className="material-symbols-outlined text-gray-300 text-4xl">check_circle</span>
              <p className="text-sm text-gray-400 mt-2">
                {filter === 'submitted' ? 'No pending entries — all caught up!' : 'No entries found.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-xs text-gray-500 uppercase tracking-wide bg-gray-50">
                    <th className="px-3 py-3 w-8">
                      <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible}
                        title="Select all visible" className="accent-[#C77D3F]" />
                    </th>
                    <th className="text-left px-5 py-3 font-semibold">Employee</th>
                    <th className="text-left px-5 py-3 font-semibold">Date</th>
                    <th className="text-left px-5 py-3 font-semibold">Hours</th>
                    <th className="text-left px-5 py-3 font-semibold hidden sm:table-cell">Job</th>
                    <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">Source</th>
                    <th className="text-left px-5 py-3 font-semibold">Status</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {visible.map(entry => (
                    <>
                      <tr
                        key={entry.id}
                        className={`hover:bg-gray-50 cursor-pointer ${selected.has(entry.id) ? 'bg-primary/5' : ''}`}
                        onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                      >
                        <td className="px-3 py-3.5" onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={selected.has(entry.id)} onChange={() => toggleRow(entry.id)}
                            className="accent-[#C77D3F]" />
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="font-medium text-gray-900">{entry.employee_name || '—'}</p>
                          <p className="text-xs text-gray-400">{entry.employee_email}</p>
                        </td>
                        <td className="px-5 py-3.5 text-gray-700">
                          {new Date(entry.work_date + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })}
                          {entry.job_title && (
                            <p className="text-xs text-gray-400 sm:hidden max-w-[120px] truncate">{entry.job_title}</p>
                          )}
                        </td>
                        <td className="px-5 py-3.5 font-semibold text-gray-900">
                          {Number(entry.hours).toFixed(1)}h
                          {entry.break_minutes > 0 && (
                            <span className="text-xs text-gray-400 font-normal ml-1">(-{entry.break_minutes}m)</span>
                          )}
                          {entry.start_time && entry.end_time && (
                            <p className="text-xs text-gray-500 font-normal whitespace-nowrap">
                              {String(entry.start_time).slice(0, 5)}–{String(entry.end_time).slice(0, 5)}
                            </p>
                          )}
                        </td>
                        <td className="px-5 py-3.5 text-gray-500 hidden sm:table-cell max-w-[180px] truncate">{entry.job_title || '—'}</td>
                        <td className="px-5 py-3.5 hidden md:table-cell">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sourceBadge[entry.source] || 'bg-gray-100 text-gray-600'}`}>
                            {entry.source}
                          </span>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge[entry.status] || 'bg-gray-100 text-gray-600'}`}>
                            {entry.status}
                          </span>
                          {!!entry.disputed && (
                            <span className="ml-1.5 text-xs px-2 py-0.5 rounded-full font-semibold bg-red-100 text-red-700" title={entry.dispute_note || ''}>
                              disputed
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3.5" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-2 justify-end flex-wrap">
                          <button
                            onClick={() => openEdit(entry)}
                            className="text-xs font-semibold text-gray-600 border border-gray-200 bg-white hover:bg-gray-50 rounded-lg px-2.5 py-1.5"
                            title="Edit this entry"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => setConfirmDelete(entry)}
                            className="text-xs font-semibold text-red-500 border border-red-200 bg-white hover:bg-red-50 rounded-lg px-2.5 py-1.5"
                            title="Delete this entry"
                          >
                            Delete
                          </button>
                          {!!entry.disputed && (
                            <button
                              onClick={async () => { try { await resolveTimeEntryDispute(entry.id); toast.success('Dispute cleared'); load(); } catch { toast.error('Failed'); } }}
                              className="text-xs font-semibold text-red-700 border border-red-200 bg-red-50 hover:bg-red-100 rounded-lg px-2.5 py-1.5"
                              title={entry.dispute_note || ''}
                            >
                              Resolve dispute
                            </button>
                          )}
                          {entry.status === 'submitted' && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handle(entry.id, 'approve')}
                                disabled={busy === entry.id}
                                className="text-xs font-semibold text-green-700 border border-green-200 bg-green-50 hover:bg-green-100 rounded-lg px-3 py-1.5 disabled:opacity-40 whitespace-nowrap"
                              >
                                {busy === entry.id ? '…' : 'Approve'}
                              </button>
                              <button
                                onClick={() => handle(entry.id, 'reject')}
                                disabled={busy === entry.id}
                                className="text-xs font-semibold text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 rounded-lg px-3 py-1.5 disabled:opacity-40"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                          </div>
                        </td>
                      </tr>
                      {expandedId === entry.id && (
                        <tr key={`${entry.id}-expanded`} className="bg-gray-50">
                          <td colSpan={8} className="px-5 py-3 border-t border-gray-100">
                            <div className="space-y-1.5">
                              {entry.notes && (
                                <p className="text-xs text-gray-600">
                                  <span className="font-semibold">Notes:</span> {entry.notes}
                                </p>
                              )}
                              {entry.submitted_at && (
                                <p className="text-xs text-gray-400">
                                  Submitted: {new Date(entry.submitted_at).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </p>
                              )}
                              {!entry.notes && !entry.submitted_at && (
                                <p className="text-xs text-gray-400 italic">No additional details.</p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modalDate && (
        <DayLogModal
          date={modalDate}
          dateLabel={gapDateLabel(modalDate)}
          employees={sortedEmployees}
          myEmployeeId={myEmployeeId}
          jobs={jobs}
          existing={entriesByDate.get(modalDate) || []}
          defaultJobId={modalJobId}
          onClose={() => setModalDate(null)}
          onSaved={handleModalSaved}
          onChanged={load}
        />
      )}
      {editEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditEntry(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-900">Edit time entry</h3>
              <button onClick={() => setEditEntry(null)} className="text-gray-400 hover:text-gray-700">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <p className="text-xs text-gray-500">{editEntry.employee_name} · currently {Number(editEntry.hours).toFixed(2)}h</p>
            {!!editEntry.disputed && editEntry.dispute_note && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-800">
                <strong>Worker dispute:</strong> {editEntry.dispute_note}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2.5">
              <label className="flex flex-col gap-1 col-span-2">
                <span className="text-xs text-gray-500">Date</span>
                <input type="date" value={editForm.work_date} onChange={e => setEditForm(f => ({ ...f, work_date: e.target.value }))}
                  className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Start</span>
                <input type="time" value={editForm.start_time} onChange={e => setEditForm(f => ({ ...f, start_time: e.target.value }))}
                  className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">End</span>
                <input type="time" value={editForm.end_time} onChange={e => setEditForm(f => ({ ...f, end_time: e.target.value }))}
                  className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Break (min)</span>
                <input type="number" min="0" step="5" value={editForm.break_minutes} onChange={e => setEditForm(f => ({ ...f, break_minutes: e.target.value }))}
                  className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">Hours</span>
                <input type="number" min="0" step="0.25" value={editForm.hours} onChange={e => setEditForm(f => ({ ...f, hours: e.target.value }))}
                  className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
              </label>
              <label className="flex flex-col gap-1 col-span-2">
                <span className="text-xs text-gray-500">Job</span>
                <select value={editForm.job_id} onChange={e => setEditForm(f => ({ ...f, job_id: e.target.value }))}
                  className="h-9 px-2 rounded-lg border border-gray-300 text-sm bg-white">
                  <option value="">No specific job</option>
                  {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1 col-span-2">
                <span className="text-xs text-gray-500">Notes</span>
                <input type="text" value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                  className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
              </label>
            </div>
            <p className="text-[11px] text-gray-400">If start & end are set, hours should match the span minus break — the value saved is what you enter here.{editEntry.disputed ? ' Saving also clears the dispute.' : ''}</p>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setEditEntry(null)} className="h-9 px-4 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={saveEdit} disabled={editBusy}
                className="h-9 px-4 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">
                {editBusy ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmDelete && (
        <ConfirmDialog
          title="Delete time entry"
          message={`Delete ${confirmDelete.employee_name}'s ${Number(confirmDelete.hours).toFixed(1)}h entry on ${String(confirmDelete.work_date).slice(0, 10)}? This cannot be undone.`}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </MainLayout>
  );
};
